import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import DataTable from "@/components/common/DataTable";
import { Loading, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate, fmtCurrency } from "@/lib/format";
import { FileSpreadsheet, FileText, Download, BarChart3 } from "lucide-react";

const MONEY = new Set(["amount", "verified_amount", "net_amount", "revenue"]);

function renderCell(key, v) {
  if (v == null || v === "") return "—";
  if (key.endsWith("_at") || key === "when") return fmtDate(v, true);
  if (MONEY.has(key)) return fmtCurrency(v);
  return String(v);
}

export default function ReportsTable({ apiParams, reportKey, onReportChange, canExport }) {
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    api.get("/reports").then(({ data }) => {
      setReports(data);
      if (!reportKey && data.length) onReportChange(data[0].key);
    }).catch((e) => setError(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!reportKey) return;
    setLoading(true); setError("");
    try { const { data } = await api.get(`/reports/${reportKey}`, { params: apiParams }); setReport(data); }
    catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [reportKey, apiParams]);
  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/reports/${reportKey}/export`, { params: { ...apiParams, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `medoho_${reportKey}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) { toast.error(apiError(e)); } finally { setExporting(""); }
  };

  const columns = (report?.columns || []).map((c) => ({ key: c.key, label: c.label, render: (r) => renderCell(c.key, r[c.key]) }));

  return (
    <div className="space-y-4" data-testid="reports-table-tab">
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
        <Select value={reportKey || ""} onValueChange={onReportChange}>
          <SelectTrigger className="w-full md:w-80 h-11" data-testid="report-select"><SelectValue placeholder="Choose a report" /></SelectTrigger>
          <SelectContent>{reports.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`report-option-${r.key}`}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        {canExport && (
          <div className="flex items-center gap-2 flex-wrap md:ml-auto">
            <Button variant="outline" onClick={() => download("csv")} disabled={!report || !!exporting} className="h-11" data-testid="export-csv-btn"><Download className="h-4 w-4" /> CSV</Button>
            <Button variant="outline" onClick={() => download("xlsx")} disabled={!report || !!exporting} className="h-11" data-testid="export-xlsx-btn"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
            <Button variant="outline" onClick={() => download("pdf")} disabled={!report || !!exporting} className="h-11" data-testid="export-pdf-btn"><FileText className="h-4 w-4" /> PDF</Button>
          </div>
        )}
      </div>
      {error ? <ErrorState message={error} onRetry={load} /> : loading || !report ? <Loading /> : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-slate-900" data-testid="report-title">{report.label}</h3>
            <span className="text-sm text-slate-500" data-testid="report-row-count">{report.total} rows</span>
          </div>
          <DataTable columns={columns} rows={report.rows} testid="report-table"
            empty={{ title: "No data for this report", description: "Try a different period, salesperson or report.", icon: BarChart3 }} />
        </div>
      )}
    </div>
  );
}
